/**
 * AssignCustomersPage — "Assign customers to [consultant]" (F6, Dashboard Access
 * UAT #10 decision (b) / Multi-Node Authentication Reference §4.3).
 *
 * Reached from Dashboard Access → the consultant's Manage. A §9 COPY-FORK of the
 * admin CustomersPage table (same search + filters + columns — never a shared
 * runtime table component): the one behavioural difference is the Actions
 * column, which carries an ASSIGN TOGGLE per row. Toggling on creates the
 * consultant grant scoped to that consultant (client-revocable — it shows in
 * the customer's access panel); toggling off removes it, and the last unassign
 * switches the org grant off.
 *
 * ⛔⛔ CONSULTANTS SERVE CUSTOMERS IN THEIR OWN DATA REGION ONLY — COMMERCIAL POLICY
 * (Jeff, 26 September 2026; ADR-116 Status note). This page therefore lists ONE zone:
 * the consultant org's own. Before 27 September 2026 it merged every zone's customers
 * and let a staff member tick a row in another jurisdiction, which could only fail —
 * and failed as "operator not found" (Dashboard Access findings §2). Now no cross-zone
 * row is rendered, the scope is stated on the page and in the Zone bar, and if a stale
 * URL or state ever produces one anyway the server's 403 is surfaced in its own words.
 *
 * ⭐ THE ORG'S ZONE IS A FACT, RESOLVED — NEVER DEFAULTED. The Manage link carries it as
 * `?zone=`. A link without one (an old bookmark) is resolved by asking every zone's
 * Dashboard Access which one holds this consultant, and only if that finds nothing does
 * the page fall back to the home zone — the behaviour it had before zones existed.
 *
 * Errors surface via the shared toast (never raw/inline); mutations run behind
 * the reauth (fresh-auth) wrapper like every Dashboard Access action.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useToast } from '../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../lib/reauth';
import { FilterPills } from '../../components/FilterPills';
import { FilterSearch } from '../../components/FilterSearch';
import {
  listCustomersInZone,
  SUBSCRIPTION_STATES,
  SUBSCRIPTION_STATE_LABELS,
  type CustomerListItem,
  type SubscriptionState,
} from '../../lib/customers-api';
import {
  listAllPortalAccess,
  listConsultantAssignments,
  assignConsultantCustomer,
  unassignConsultantCustomer,
} from '../../lib/portal-access-api';
import { HOME_ZONE, zoneByCode, type PortalZone } from '../../lib/zones';
import { useZoneReadReport } from '../../components/ZoneContext';
import { ZoneCell } from '../../components/ZoneIndicators';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly:
// it carries the status, the path and the response body.
import { friendlyFromUnknown } from '../../lib/friendly-error';
import { formatDate } from '../../lib/time-format';

type SortKey = 'businessName' | 'plan' | 'subscriptionState' | 'createdAt';
type SortDir = 'asc' | 'desc';

export function AssignCustomersPage() {
  const me = useStaffMe();
  const { sub } = useParams<{ sub: string }>();
  const [params] = useSearchParams();
  const orgId = params.get('orgId') ?? '';
  const consultantName = params.get('name') ?? 'this consultant';
  const urlZone = params.get('zone');

  if (me.status === 'loading') {
    return <main className="portal-page"><p className="portal-muted">Loading…</p></main>;
  }
  if (me.status === 'error') {
    return (
      <main className="portal-page">
        <h1>Assign customers</h1>
        <div className="portal-error" role="alert">{me.message}</div>
      </main>
    );
  }
  if (!sub || !orgId) {
    return (
      <main className="portal-page">
        <h1>Assign customers</h1>
        <div className="portal-error" role="alert">
          Missing consultant reference. Open this page from User Management → Consultants → the consultant’s Assign.
        </div>
      </main>
    );
  }

  return <AssignSurface sub={sub} orgId={orgId} consultantName={consultantName} urlZone={urlZone} />;
}

// ---------------------------------------------------------------------------

/**
 * Which zone holds this consultant's org.
 *
 * `?zone=` from the Manage link answers at once. Without it, every zone's Dashboard Access
 * is asked which one lists this person in this org — the same fan-out the tab itself runs —
 * so an old link still lands on the right jurisdiction. Nothing found ⇒ home, which is
 * what this page always did before zones; the server's guard still refuses a wrong guess.
 */
function useConsultantOrgZone(sub: string, orgId: string, urlZone: string | null): PortalZone | null {
  const [resolved, setResolved] = useState<PortalZone | null>(() => (urlZone ? (zoneByCode(urlZone) ?? HOME_ZONE) : null));
  useEffect(() => {
    if (urlZone) { setResolved(zoneByCode(urlZone) ?? HOME_ZONE); return; }
    let cancelled = false;
    (async () => {
      let zone: PortalZone = HOME_ZONE;
      try {
        const { portals } = await listAllPortalAccess();
        const org = portals.find((p) =>
          p.orgId === orgId && (p.operators ?? []).some((o) => o.cognitoSub === sub));
        if (org?.zone) zone = zoneByCode(org.zone) ?? HOME_ZONE;
      } catch {
        // Unresolvable ⇒ home; a wrong guess is refused server-side, never written.
      }
      if (!cancelled) setResolved(zone);
    })();
    return () => { cancelled = true; };
  }, [sub, orgId, urlZone]);
  return resolved;
}

function AssignSurface({ sub, orgId, consultantName, urlZone }: {
  sub: string; orgId: string; consultantName: string; urlZone: string | null;
}) {
  const orgZone = useConsultantOrgZone(sub, orgId, urlZone);
  if (!orgZone) {
    return (
      <main className="portal-page">
        <h1>Assign customers to {consultantName}</h1>
        <p className="portal-muted">Resolving the consultant’s data region…</p>
      </main>
    );
  }
  return <AssignTable sub={sub} orgId={orgId} consultantName={consultantName} orgZone={orgZone} />;
}

function AssignTable({ sub, orgId, consultantName, orgZone }: {
  sub: string; orgId: string; consultantName: string; orgZone: PortalZone;
}) {
  const toast = useToast();
  const reauth = useReAuth();

  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [busyAccount, setBusyAccount] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [subStateFilter, setSubStateFilter] = useState<Set<SubscriptionState>>(
    new Set(SUBSCRIPTION_STATES),
  );
  const [planFilter, setPlanFilter] = useState('');
  const [betaFilter, setBetaFilter] = useState<'all' | 'only' | 'hide'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'businessName',
    dir: 'asc',
  });

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      // ⭐ ONE ZONE, BOTH READS — the org's. The customer list and its assignments are read
      // from the same jurisdiction, so a row and its "Assigned" state can never come from
      // different places (the defect the earlier fan-out had to be repaired for).
      const [all, assignments] = await Promise.all([
        listCustomersInZone(orgZone),
        listConsultantAssignments(sub, orgId, orgZone),
      ]);
      setItems(all);
      setAssigned(new Set(assignments.map((a) => a.clientAccountId)));
      setLoadState('loaded');
    } catch (err) {
      const msg = friendlyFromUnknown(err);
      setLoadError(msg);
      setLoadState('error');
      toast.push({ variant: 'error', title: 'Could not load customers', detail: msg });
    }
  }, [sub, orgId, orgZone, toast]);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — the Zone bar states the scope: "Customers: <org zone> only".
  useZoneReadReport({
    rows: loadState === 'loaded' ? items : null, failedZones: [], filter: orgZone.code, scope: 'Customers',
  });

  async function toggleAssign(c: CustomerListItem, next: boolean): Promise<void> {
    setBusyAccount(c.accountId);
    try {
      // Addressed to the ORG's zone, carrying the zone this row was read from — the server
      // refuses a mismatch by policy before any lookup (`cross_zone_assignment`).
      const accountZone = c.zone ?? orgZone.code;
      if (next) {
        await reauth.withReauth(() => assignConsultantCustomer(sub, orgId, c.accountId, orgZone, accountZone));
        setAssigned((prev) => new Set(prev).add(c.accountId));
        toast.push({
          variant: 'success',
          title: `Assigned — ${c.businessName}`,
          detail: `${consultantName} now sees this customer in their book. The customer's access panel shows the consultant access, and they can switch it off at any time.`,
        });
      } else {
        await reauth.withReauth(() => unassignConsultantCustomer(sub, orgId, c.accountId, orgZone, accountZone));
        setAssigned((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(c.accountId);
          return nextSet;
        });
        toast.push({
          variant: 'success',
          title: `Unassigned — ${c.businessName}`,
          detail: `${consultantName} no longer sees this customer.`,
        });
      }
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      // #21 — MATCH on the raw message, DISPLAY only the mapped sentence. The
      // policy refusal (`cross_zone_assignment`) maps to the server's own words.
      const friendly = friendlyFromUnknown(err);
      toast.push({ variant: 'error', title: 'Assignment failed', detail: friendly });
    } finally {
      setBusyAccount(null);
    }
  }

  const subStateCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const s of SUBSCRIPTION_STATES) c[s] = 0;
    items.forEach((i) => { if (c[i.subscriptionState] !== undefined) c[i.subscriptionState]++; });
    return c;
  }, [items]);

  const subStatePills = useMemo(
    () => SUBSCRIPTION_STATES.map((s) => ({
      key: s,
      label: SUBSCRIPTION_STATE_LABELS[s],
      count: subStateCounts[s] ?? 0,
    })),
    [subStateCounts],
  );

  const plans = useMemo(
    () => Array.from(new Set(items.map((i) => i.plan).filter(Boolean))).sort(),
    [items],
  );

  const betaCount = useMemo(() => items.filter((i) => i.beta).length, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((c) => {
      if (betaFilter === 'only' && !c.beta) return false;
      if (betaFilter === 'hide' && c.beta) return false;
      if (!subStateFilter.has(c.subscriptionState)) return false;
      if (planFilter && c.plan !== planFilter) return false;
      if (q) {
        const hay = `${c.businessName} ${c.email} ${c.customerNumber} ${c.consultant}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sort.key].toLowerCase?.() ?? a[sort.key];
      const bv = b[sort.key].toLowerCase?.() ?? b[sort.key];
      return av < bv ? -1 * factor : av > bv ? 1 * factor : 0;
    });
  }, [items, search, subStateFilter, planFilter, betaFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const sortArrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕');

  return (
    <main className="portal-page portal-customers-page">
      <header className="portal-partners-header">
        <div>
          <h1>Assign customers to {consultantName}</h1>
          <p className="portal-partners-subtitle">
            Toggle a customer on to add them to this consultant’s book. Assigning creates
            consultant access the customer can see — and switch off — in their access panel.
            Only customers in <b>{orgZone.label}</b> are listed — consultants serve their own
            data region. Back to <Link to="/admin/users?tab=consultants">User Management → Consultants</Link>.
          </p>
        </div>
      </header>

      <div className="portal-applications-controls">
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, email or CN…"
          ariaLabel="Search customers"
        />
        <FilterPills
          groups={subStatePills}
          selected={subStateFilter}
          onChange={(next) => setSubStateFilter(next as Set<SubscriptionState>)}
          ariaLabel="Filter by subscription state"
        />
      </div>

      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Plan
          <select
            className="portal-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All plans</option>
            {plans.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="portal-users-filter-label">
          Beta
          <select
            className="portal-select"
            value={betaFilter}
            onChange={(e) => setBetaFilter(e.target.value as 'all' | 'only' | 'hide')}
          >
            <option value="all">Include{betaCount > 0 ? ` (${betaCount})` : ''}</option>
            <option value="only">Beta only</option>
            <option value="hide">Hide beta</option>
          </select>
        </label>
        {/* ⛔ NOT A ZONE FILTER. The scope is fixed by policy and stated, not chosen — a
            control here would offer a choice the platform refuses (ADR-123 wording style:
            region first, the reason in plain words). */}
        <span
          className="portal-users-filter-label portal-assign-scope"
          data-assign-scope={orgZone.code}
          role="status"
        >
          Customers in {orgZone.label} only — consultants serve their own data region
        </span>
        <span className="portal-muted portal-assign-count" aria-live="polite">
          {assigned.size} assigned
        </span>
      </div>

      {loadState === 'loading' && <p className="portal-muted">Loading customers…</p>}
      {loadState === 'error' && (
        <div className="portal-error" role="alert">
          Could not load customers. {loadError}{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && (
        <table className="portal-users-table portal-customers-table">
          <thead>
            <tr>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('businessName')}>
                  Business name{sortArrow('businessName')}
                </button>
              </th>
              <th scope="col">Zone</th>
              <th scope="col">Primary domain</th>
              <th scope="col">Email</th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('plan')}>
                  Plan{sortArrow('plan')}
                </button>
              </th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('subscriptionState')}>
                  Subscription{sortArrow('subscriptionState')}
                </button>
              </th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('createdAt')}>
                  Created{sortArrow('createdAt')}
                </button>
              </th>
              <th scope="col" className="portal-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="portal-users-empty">
                  {subStateFilter.size === 0
                    ? 'No filter pills selected — click a subscription state pill to show customers.'
                    : `No customers in ${orgZone.label} match the current filters.`}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const isAssigned = assigned.has(c.accountId);
              return (
                <tr key={c.accountId} data-customer-zone={c.zone ?? ''}>
                  {/* `title` on every truncated cell: the columns are capped so the
                      Actions column stays on screen, and a cap without a tooltip would
                      simply hide the end of a long business name or email. */}
                  <td>
                    <div className="portal-users-name" title={c.businessName}>{c.businessName}</div>
                    <div className="portal-customers-cn-sub">{c.customerNumber || '—'}</div>
                  </td>
                  <td><ZoneCell zone={c.zone} /></td>
                  <td className="portal-customers-domain" title={c.primaryDomain}>{c.primaryDomain || '—'}</td>
                  <td className="portal-customers-email" title={c.email}>{c.email}</td>
                  <td className="portal-customers-plan">
                    {c.beta
                      ? <span className="portal-beta-chip beta-chip-beta">Beta</span>
                      : (c.plan || '—')}
                  </td>
                  <td>
                    <span className={`portal-badge portal-badge-${c.subscriptionState}`}>
                      {SUBSCRIPTION_STATE_LABELS[c.subscriptionState]}
                    </span>
                  </td>
                  <td className="portal-users-created">
                    {c.createdAt ? formatDate(c.createdAt) : '—'}
                  </td>
                  <td className="portal-users-row-actions portal-actions-col">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isAssigned}
                      aria-label={`${isAssigned ? 'Unassign' : 'Assign'} ${c.businessName}`}
                      className={`portal-assign-toggle${isAssigned ? ' on' : ''}`}
                      disabled={busyAccount === c.accountId}
                      onClick={() => void toggleAssign(c, !isAssigned)}
                    >
                      <span className="portal-assign-toggle-knob" aria-hidden="true" />
                      <span className="portal-assign-toggle-label">
                        {busyAccount === c.accountId ? '…' : isAssigned ? 'Assigned' : 'Assign'}
                      </span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
